import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Loader2, MessageCircle, Phone, Eye, CheckCircle, Clock, AlertTriangle } from 'lucide-react';

export const RinnoviTab = ({ rinnovi, loading, contactingId, onContatta, onDetail }) => (
  <div className="space-y-4" data-testid="rinnovi-tab">
    <div className="flex items-center justify-between">
      <div>
        <h3 className="font-semibold text-slate-800">Rinnovi Cessioni del Quinto</h3>
        <p className="text-xs text-slate-500 mt-0.5">Pratiche CQP/CQS con calcolo automatico rinnovabilita</p>
      </div>
      <div className="flex items-center gap-3 text-xs">
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-500"></span> Rinnovabile</div>
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span> In avvicinamento</div>
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-slate-300"></span> Non ancora</div>
      </div>
    </div>

    {loading ? (
      <div className="text-center py-12"><Loader2 className="animate-spin mx-auto text-slate-400" size={24} /></div>
    ) : rinnovi.length === 0 ? (
      <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
        <RefreshCw size={32} className="mx-auto text-slate-300 mb-2" />
        <p className="text-sm text-slate-500">Nessuna pratica CQ erogata con dati rinnovo</p>
        <p className="text-xs text-slate-400 mt-1">Imposta durata e data decorrenza nelle pratiche CQP/CQS</p>
      </div>
    ) : (
      <div className="space-y-3">
        {rinnovi.map(p => {
          const r = p.rinnovo || {};
          const rinnovabile = r.rinnovo_disponibile;
          const mesiMancanti = r.mesi_mancanti || 0;
          const prossimo = !rinnovabile && mesiMancanti <= 6;
          const progress = r.rate_necessarie > 0 ? Math.min(100, (r.rate_pagate / r.rate_necessarie) * 100) : (rinnovabile ? 100 : 0);
          return (
            <div key={p.id} className={`bg-white rounded-xl border-2 p-4 transition-all ${
              rinnovabile ? 'border-green-300 shadow-sm shadow-green-100' : prossimo ? 'border-amber-200' : 'border-slate-200'
            }`} data-testid={`rinnovo-card-${p.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-slate-900">{p.nome_cliente} {p.cognome_cliente}</h4>
                    <Badge className="text-[10px] bg-blue-50 text-blue-700">{p.tipo}</Badge>
                    {rinnovabile && (
                      <Badge className="bg-green-100 text-green-700 text-[10px] animate-pulse" data-testid={`badge-rinnovabile-${p.id}`}>
                        <CheckCircle size={10} className="mr-0.5" /> RINNOVABILE
                      </Badge>
                    )}
                    {prossimo && !rinnovabile && (
                      <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                        <Clock size={10} className="mr-0.5" /> Tra {mesiMancanti} mesi
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                    {p.compagnia && <span>{p.compagnia}</span>}
                    <span>{p.durata_mesi} mesi</span>
                    {p.rata_mensile && <span>Rata: {Number(p.rata_mensile).toFixed(2)} EUR</span>}
                    {p.importo_erogato && <span>Erogato: {Number(p.importo_erogato).toLocaleString('it-IT')} EUR</span>}
                    {r.rinnovo_data && <span>Data rinnovo: <strong className="text-slate-700">{r.rinnovo_data}</strong></span>}
                  </div>
                  {/* Progress bar */}
                  {r.rate_necessarie > 0 && (
                    <div className="mt-3">
                      <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                        <span>Rate pagate: {r.rate_pagate}/{r.rate_totali}</span>
                        <span>{r.rate_necessarie > 0 ? `${Math.round(progress)}% di ${r.rate_necessarie} necessarie` : ''}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${rinnovabile ? 'bg-green-500' : prossimo ? 'bg-amber-400' : 'bg-slate-300'}`}
                          style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mt-2 italic">{r.rinnovo_info}</p>
                  {/* Conteggio Estintivo */}
                  {r.conteggio_estintivo && (
                    <div className="mt-3 bg-white border border-slate-200 rounded-lg p-3">
                      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Conteggio Estintivo Stimato</p>
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div>
                          <p className="text-[10px] text-slate-400">Lordo Totale</p>
                          <p className="text-sm font-bold text-slate-700">{Number(r.conteggio_estintivo.lordo_totale).toLocaleString('it-IT', {minimumFractionDigits: 2})} EUR</p>
                          <p className="text-[8px] text-slate-400">{r.conteggio_estintivo.rate_restanti} rate × rata</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-400">Netto Estinzione</p>
                          <p className="text-sm font-bold text-rose-700">{Number(r.conteggio_estintivo.saldo_estinzione).toLocaleString('it-IT', {minimumFractionDigits: 2})} EUR</p>
                          <p className="text-[8px] text-slate-400">capitale + spese {r.conteggio_estintivo.perc_spese || 1}%</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-400">Spese Estinzione</p>
                          <p className="text-sm font-bold text-slate-500">{Number(r.conteggio_estintivo.spese_estinzione).toLocaleString('it-IT', {minimumFractionDigits: 2})} EUR</p>
                          <p className="text-[8px] text-slate-400">{r.conteggio_estintivo.perc_spese || 1}% su residuo</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-1">
                          <p className="text-[10px] text-green-600">Risparmio Interessi</p>
                          <p className="text-sm font-bold text-green-700">{Number(r.conteggio_estintivo.risparmio_interessi || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} EUR</p>
                          <p className="text-[8px] text-green-500">lordo - netto</p>
                        </div>
                      </div>
                      <p className="text-[9px] text-slate-400 mt-1.5 text-right">{r.conteggio_estintivo.metodo}</p>
                    </div>
                  )}
                </div>
                {/* Contact buttons */}
                <div className="flex flex-col gap-1.5 shrink-0">
                  {p.telefono && rinnovabile && (
                    <>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs w-full"
                        onClick={() => onContatta(p, 'whatsapp')}
                        disabled={contactingId === p.id}
                        data-testid={`rinnovo-wa-${p.id}`}>
                        {contactingId === p.id ? <Loader2 size={13} className="animate-spin mr-1" /> : <MessageCircle size={13} className="mr-1" />}
                        WhatsApp
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs w-full"
                        onClick={() => onContatta(p, 'telefono')}
                        data-testid={`rinnovo-call-${p.id}`}>
                        <Phone size={13} className="mr-1" /> Chiama
                      </Button>
                    </>
                  )}
                  {!p.telefono && rinnovabile && (
                    <p className="text-[10px] text-red-500 text-center"><AlertTriangle size={11} className="inline mr-0.5" />No telefono</p>
                  )}
                  <Button size="sm" variant="ghost" className="text-xs" onClick={() => onDetail(p)}>
                    <Eye size={13} className="mr-1" /> Dettaglio
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);
